import { FunctionComponent } from "react";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { useAppSelector } from "@/redux/hooks";
import { contactSelector } from "@/pages/Private/redux/contact/contact.slice";
import { useGetContactProspectDetailQuery } from "@/pages/Private/redux/contact/contact.api";

import { FeedCompanyIdentity } from "./FeedCompanyIdentity";
import { FeedPersonIdentity } from "./FeedPersonIdentity";
import { FeedSelectProspect } from "./FeedSelectProspect";

interface RunEntityHeaderProps {
	card: FeedRunCard;
	/** Currently selected tracked person; null on a degraded company-only card (D7). */
	selectedPerson: FeedPerson | null;
	/** True while the Feed is fetching — an add must not read that fetch as its answer. */
	isFeedFetching?: boolean;
	onSelectPerson: (listPersonId: number) => void;
}

/**
 * Entity header of a run card: the shared lead-panel identity cards (person +
 * company) hydrated from the selected person's prospect detail, with the
 * feed-only person switcher. Until the detail lands, both cards render from
 * the card's own lean fields, then upgrade in place.
 */
export const RunEntityHeader: FunctionComponent<RunEntityHeaderProps> = ({
	card,
	selectedPerson,
	isFeedFetching = false,
	onSelectPerson,
}) => {
	// Placeholder leads have only a dummy prospect — don't fetch a detail for it.
	const prospectId = selectedPerson?.needsProspectSelection
		? null
		: (selectedPerson?.prospectId ?? null);

	useGetContactProspectDetailQuery(prospectId ?? 0, { skip: !prospectId });
	const { prospectDetailsById } = useAppSelector(contactSelector);
	const detail = prospectId ? prospectDetailsById[prospectId] : undefined;

	return (
		<div className="flex flex-col gap-2.5 tb:flex-row">
			{selectedPerson &&
				(selectedPerson.needsProspectSelection ? (
					<FeedSelectProspect
						key={selectedPerson.listPersonId}
						card={card}
						person={selectedPerson}
					/>
				) : (
					<FeedPersonIdentity
						key={selectedPerson.listPersonId}
						card={card}
						detail={detail}
						isFeedFetching={isFeedFetching}
						person={selectedPerson}
						onSelectPerson={onSelectPerson}
					/>
				))}
			<FeedCompanyIdentity company={card.company} organization={detail?.organization} />
		</div>
	);
};
